import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { ArrowRight, Sparkles, Shield, Zap, Globe, ChevronRight, Star, Briefcase, Users, Award, MapPin, ChevronDown } from 'lucide-react'

const heroStats = [
  { icon: Briefcase, value: '100+', label: 'Government Projects' },
  { icon: Users, value: '50+', label: 'Government Clients' },
  { icon: Award, value: '15+', label: 'Years Excellence' },
  { icon: MapPin, value: '20+', label: 'Districts Served' },
]

const features = [
  { icon: Zap, text: 'Fast Delivery', color: '#6366f1' },
  { icon: Star, text: 'ISO Certified', color: '#f59e0b' },
  { icon: Award, text: '15+ Years', color: '#10b981' },
]

const serviceTags = [
  'Government Technology',
  'Digital Infrastructure',
  'Smart Governance',
  'Healthcare IT',
]

export const HeroSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-16 lg:pt-20">
      {/* Background is now global - in Home.tsx */}
      {/* Matrix-style Grid Pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: `
            linear-gradient(rgba(99, 102, 241, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(99, 102, 241, 0.03) 1px, transparent 1px),
            radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.02) 0%, transparent 50%)
          `,
          backgroundSize: '80px 80px, 80px 80px, 100% 100%',
        }}
      />

      {/* Animated Gradient Orbs */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5, x: 50 }}
        animate={{ opacity: 0.6, scale: 1, x: 0 }}
        transition={{ duration: 1.5, ease: 'easeOut' }}
        className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.4) 0%, rgba(139, 92, 246, 0.2) 30%, transparent 60%)',
          transform: 'translate(30%, -30%)',
        }}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.5, x: -50 }}
        animate={{ opacity: 0.5, scale: 1, x: 0 }}
        transition={{ duration: 1.5, ease: 'easeOut', delay: 0.2 }}
        className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(236, 72, 153, 0.3) 0%, rgba(139, 92, 246, 0.15) 30%, transparent 60%)',
          transform: 'translate(-30%, 30%)',
        }}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.3 }}
        animate={{ opacity: 0.4, scale: 1 }}
        transition={{ duration: 2, ease: 'easeOut', delay: 0.4 }}
        className="absolute top-1/2 left-1/2 w-[500px] h-[500px] rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.25) 0%, transparent 50%)',
          transform: 'translate(-50%, -50%)',
        }}
      />

      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.1, 0.4, 0.1],
              y: [0, -30, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
            className="absolute w-1 h-1 rounded-full"
            style={{
              background: i % 4 === 0 ? '#6366f1' : i % 4 === 1 ? '#8b5cf6' : i % 4 === 2 ? '#ec4899' : '#06b6d4',
              left: `${5 + Math.random() * 90}%`,
              top: `${5 + Math.random() * 90}%`,
              boxShadow: `0 0 10px ${i % 4 === 0 ? '#6366f1' : i % 4 === 1 ? '#8b5cf6' : i % 4 === 2 ? '#ec4899' : '#06b6d4'}`,
            }}
          />
        ))}
      </div>

      <div className="container-enterprise relative z-10 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto">
          {/* Service Tags Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="flex flex-wrap gap-3 mb-8"
          >
            {serviceTags.map((tag, index) => (
              <motion.span
                key={tag}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="px-5 py-2 text-sm font-medium rounded-full cursor-default"
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  color: '#e0e7ff',
                  backdropFilter: 'blur(10px)',
                }}
              >
                {tag}
              </motion.span>
            ))}
          </motion.div>

          {/* Premium Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-8"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
                boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
              }}
            >
              <div className="relative">
                <Sparkles className="w-5 h-5 text-primary" />
                <motion.div
                  className="absolute inset-0"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{
                    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.5) 0%, transparent 70%)',
                  }}
                />
              </div>
              <span className="text-sm font-medium text-white">Trusted by Government Organizations</span>
              <ChevronRight className="w-4 h-4 text-primary" />
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight mb-6"
          >
            <span className="text-white">Engineering Innovation</span>
            <br />
            <span className="gradient-text-animated">Delivering Excellence</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-lg sm:text-xl text-slate-400 max-w-2xl mb-10 leading-relaxed"
          >
            Premier Government Technology & Infrastructure Enterprise. Trusted partner for
            <span className="text-primary font-semibold"> Government, Education & Healthcare </span>
            sectors across Maharashtra and India.
          </motion.p>

          {/* Feature Pills */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="flex flex-wrap gap-3 mb-10"
          >
            {features.map((feature) => (
              <motion.div
                key={feature.text}
                whileHover={{ scale: 1.05 }}
                className="flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-all duration-300"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <feature.icon className="w-4 h-4" style={{ color: feature.color }} />
                <span className="text-sm text-slate-300">{feature.text}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap gap-4 mb-16"
          >
            <Link
              to="/contact"
              className="btn-glow inline-flex items-center gap-3 px-8 py-4 text-white font-semibold"
            >
              <Sparkles className="w-5 h-5" />
              <span>Start Your Project</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/projects"
              className="btn-secondary inline-flex items-center gap-2 px-8 py-4 text-white font-semibold rounded-2xl"
            >
              <span>View Our Work</span>
              <span className="text-sm text-slate-500">(100+)</span>
            </Link>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
          >
            {heroStats.map((stat, index) => {
              const Icon = stat.icon
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -4 }}
                  className="group relative overflow-hidden rounded-2xl p-5"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: 'radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
                    }}
                  />

                  <div className="relative flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                      style={{
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                        border: '1px solid rgba(99, 102, 241, 0.2)',
                      }}
                    >
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl sm:text-3xl font-bold text-white mb-0.5">{stat.value}</p>
                      <p className="text-sm text-slate-400">{stat.label}</p>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </motion.div>
        </div>

        {/* Right Side Visual - Premium Dashboard */}
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="hidden xl:block absolute right-8 top-[15%] w-[400px]"
        >
          {/* Main Dashboard Card */}
          <div className="relative rounded-2xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 0 60px rgba(99, 102, 241, 0.15), 0 20px 40px -12px rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(24px)',
            }}
          >
            {/* Window Header */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b"
              style={{ borderColor: 'rgba(255, 255, 255, 0.06)' }}
            >
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ef4444' }} />
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#f59e0b' }} />
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#10b981' }} />
              <span className="ml-3 text-[10px] text-slate-500 font-mono">eonixa-dynamics.io</span>
              <div className="ml-auto flex items-center gap-1.5">
                <motion.div
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: '#10b981' }}
                />
                <span className="text-[10px] text-slate-500">Live</span>
              </div>
            </div>

            {/* Dashboard Content */}
            <div className="p-5">
              {/* Header */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    boxShadow: '0 0 20px rgba(99, 102, 241, 0.5)',
                  }}
                >
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Command Center</h3>
                  <p className="text-xs text-slate-400">Real-time Project Network</p>
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-2 mb-5">
                {[
                  { label: 'Active Users', value: '12.5K', color: '#6366f1' },
                  { label: 'Projects', value: '47', color: '#8b5cf6' },
                  { label: 'Success', value: '99%', color: '#10b981' },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="p-3 rounded-xl"
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <p className="text-[10px] text-slate-500 mb-0.5">{stat.label}</p>
                    <p className="text-lg font-bold" style={{ color: stat.color }}>{stat.value}</p>
                  </div>
                ))}
              </div>

              {/* Chart Visualization */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-white">Performance</h4>
                  <span className="text-[10px] text-primary">Live</span>
                </div>
                <div className="h-20 flex items-end gap-1">
                  {[65, 45, 80, 55, 90, 70, 85, 60, 75, 95].map((height, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      animate={inView ? { height: `${height}%` } : {}}
                      transition={{ duration: 0.5, delay: 0.5 + i * 0.05 }}
                      className="flex-1 rounded-t"
                      style={{
                        background: `linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%)`,
                      }}
                    />
                  ))}
                </div>
                <div className="flex justify-between mt-1.5">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                    <span key={day} className="text-[9px] text-slate-600">{day}</span>
                  ))}
                </div>
              </div>

              {/* Mini Stats */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { icon: Briefcase, value: '₹100Cr+', label: 'Value' },
                  { icon: Users, value: '50+', label: 'Clients' },
                  { icon: Award, value: '4', label: 'ISO' },
                ].map((stat) => {
                  const Icon = stat.icon
                  return (
                    <div
                      key={stat.label}
                      className="rounded-xl p-3 text-center"
                      style={{
                        background: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid rgba(255, 255, 255, 0.06)',
                      }}
                    >
                      <Icon className="w-4 h-4 text-primary mx-auto mb-1" />
                      <p className="text-sm font-bold text-white">{stat.value}</p>
                      <p className="text-[10px] text-slate-400">{stat.label}</p>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Gradient Border Glow */}
            <div className="absolute inset-0 rounded-2xl pointer-events-none opacity-50"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 50%, rgba(236, 72, 153, 0.05) 100%)',
              }}
            />
          </div>

          {/* Floating Badges */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: -20 }}
            animate={inView ? { opacity: 1, scale: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="absolute -left-4 top-20 px-3 py-2 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, #312e81 0%, #4338ca 100%)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              boxShadow: '0 0 25px rgba(99, 102, 241, 0.3)',
            }}
          >
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium text-white">ISO 27001</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: -20 }}
            animate={inView ? { opacity: 1, scale: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 1 }}
            className="absolute -left-4 bottom-20 px-3 py-2 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(5, 150, 105, 0.3) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              boxShadow: '0 0 25px rgba(16, 185, 129, 0.2)',
            }}
          >
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-medium text-white">5 Star Rated</span>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2 text-slate-500">
          <span className="text-xs uppercase tracking-wider">Scroll</span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </div>
      </motion.div>
    </section>
  )
}